import axios, { AxiosInstance } from 'axios';
import { BaseModel, TariffConfiguration, Scenario, KappaConfig, hasTariffShock, hasKappaShock } from '../types/model';
import { getBackendUrl, getBackendUrlSync } from './backendUrl';
import { SimulationRun, SimulationRunRequest } from '../types/policy';

const POLL_TIMEOUT_MS = 20 * 60 * 1000;
const POLL_INTERVAL_MS = 3000;

interface SavedScenario {
  id: string;
  name: string;
  tariff_config: TariffConfiguration;
  kappa_config?: KappaConfig;
  result: BaseModel;
  created_at: string;
}

class ApiService {
  private client: AxiosInstance;
  constructor() {
    this.client = axios.create({ baseURL: getBackendUrlSync(), timeout: 5 * 60 * 1000 });
    this.client.interceptors.request.use(async config => {
      config.baseURL = await getBackendUrl();
      return config;
    });
  }
  async init() { await getBackendUrl(); }
  async getFrameworkInfo(): Promise<Record<string, { supports_kappa: boolean }>> {
    return (await this.client.get('/models/framework-info')).data;
  }
  async loadCalibration(framework: string, dataset: string): Promise<BaseModel> {
    return (await this.client.post('/models/calibration', { model_framework: framework, dataset })).data;
  }
  async loadBaselineModel(framework: string, dataset: string): Promise<BaseModel> {
    return (await this.client.post('/models/load', { model_framework: framework, dataset })).data;
  }
  private scenarioConfig(tariff?: TariffConfiguration, kappa?: KappaConfig) {
    return { tariff_config: hasTariffShock(tariff) ? tariff : undefined,
      kappa_config: hasKappaShock(kappa) ? kappa : undefined };
  }
  private async job(path: string, body: unknown, statusPath: string) {
    const { job_id } = (await this.client.post(path, body)).data;
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      const status = (await this.client.get(`${statusPath}/${job_id}`)).data;
      if (status.status === 'done') return { job_id, result: status.result };
      if (status.status === 'error') throw new Error(status.error);
    }
    throw new Error('The request timed out; the server job may still be running.');
  }
  async solveCounterfactual(framework: string, dataset: string, tariff?: TariffConfiguration, kappa?: KappaConfig): Promise<BaseModel> {
    const job = await this.job('/models/counterfactual', {
      model_framework: framework, dataset, ...this.scenarioConfig(tariff, kappa),
    }, '/models/counterfactual/status');
    return job.result;
  }
  async rebuildBaseline(framework: string, dataset: string): Promise<{ status: string; framework: string; dataset: string; size_kb?: number }> {
    return (await this.client.post('/cache/rebuild', { model_framework: framework, dataset })).data;
  }
  private async exportFile(path: string, body: unknown): Promise<Blob> {
    const job = await this.job(path, body, '/models/export/status');
    return (await this.client.get(`/models/export/download/${job.job_id}`, {
      responseType: 'blob', timeout: POLL_TIMEOUT_MS,
    })).data;
  }
  async downloadBaselineExcel(framework: string, dataset: string) {
    return this.exportFile('/models/export/baseline', { model_framework: framework, dataset });
  }
  async downloadScenariosExcel(framework: string, dataset: string, scenarios: Scenario[]) {
    return this.exportFile('/models/export/scenarios', {
      model_framework: framework, dataset,
      scenarios: scenarios.filter(s => s.status === 'solved').map(s => ({ name: s.name,
        ...this.scenarioConfig(s.tariffConfig, s.kappaConfig) })),
    });
  }
  async startPolicyRun(request: SimulationRunRequest): Promise<SimulationRun> {
    return (await this.client.post('/simulation-runs', request)).data;
  }
  async getPolicyRun(id: string): Promise<SimulationRun> {
    return (await this.client.get(`/simulation-runs/${encodeURIComponent(id)}`)).data;
  }
  async listCachedScenarios(framework: string, dataset: string): Promise<{scenarios: Pick<SavedScenario, 'id' | 'name' | 'created_at'>[]}> {
    return (await this.client.get('/cache/scenarios', { params: { model_framework: framework, dataset } })).data;
  }
  async getCachedScenario(framework: string, dataset: string, id: string): Promise<{ scenario: SavedScenario }> {
    return (await this.client.get(`/cache/scenario/${encodeURIComponent(id)}`, { params: { model_framework: framework, dataset } })).data;
  }
  async saveScenarioToCache(framework: string, dataset: string, scenario: Scenario): Promise<{ success: boolean; id: string }> {
    return (await this.client.post('/cache/scenario', { model_framework: framework, dataset,
      scenario: { id: scenario.id, name: scenario.name, tariff_config: scenario.tariffConfig,
        kappa_config: scenario.kappaConfig, result: scenario.result, created_at: scenario.createdAt },
    })).data;
  }
  async deleteCachedScenario(framework: string, dataset: string, id: string): Promise<{ success: boolean }> {
    return (await this.client.delete(`/cache/scenario/${encodeURIComponent(id)}`, { params: { model_framework: framework, dataset } })).data;
  }
  async clearAllCache(framework: string, dataset: string): Promise<{ success: boolean }> {
    return (await this.client.delete('/cache/all', { params: { model_framework: framework, dataset } })).data;
  }
}
export const apiService = new ApiService();
